import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './shared-components/header/header.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { HomeComponent } from './pages/home/home.component';
import { MeetingCardComponent } from './shared-components/meeting-card/meeting-card.component';
import { ContactUsComponent } from './shared-components/contact-us/contact-us.component';
import { FooterComponent } from './shared-components/footer/footer.component';
import { LoginPopupComponent } from './shared-components/login-popup/login-popup.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgOtpInputModule } from 'ng-otp-input';
import { NgxPermissionsModule } from 'ngx-permissions';
import {
  RECAPTCHA_V3_SITE_KEY,
  ReCaptchaV3Service,
  RecaptchaModule,
} from 'ng-recaptcha';
import { EventDetailsComponent } from './pages/event-details/event-details.component';
import { SafeHTMLPipe } from './services/pipes/safe-html.pipe';
import { LocalizedDatePipe } from './services/pipes/localized-date.pipe';
import { ToVATPipe } from './services/pipes/to-vat.pipe';
import { CommonModule } from '@angular/common';
import {
  NgbCarouselModule,
  NgbDropdownModule,
  NgbToastModule,
} from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerModule } from 'ngx-spinner';
import { EventoInputComponent } from './shared-components/evento-input/evento-input.component';
import { ErrorInputComponent } from './shared-components/error-input/error-input.component';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NewBookingComponent } from './pages/book/new-booking/new-booking.component';
import { BookTitleComponent } from './pages/book/book-title/book-title.component';
import { BookFooterComponent } from './pages/book/book-footer/book-footer.component';
import { CartComponent } from './pages/book/cart/cart.component';
import { CartItemComponent } from './pages/book/cart-item/cart-item.component';
import { CustomFormsModule } from 'ngx-custom-validators';
import { CustomFormComponent } from './pages/book/custom-form/custom-form.component';
import { ErrorMessageComponent } from './pages/book/error-message/error-message.component';
import { NewChooseTicketComponent } from './pages/book/new-choose-ticket/new-choose-ticket.component';
import { ReviewCartComponent } from './pages/book/review-cart/review-cart.component';
import { ReviewDetailsComponent } from './pages/book/review-details/review-details.component';
import { ReviewDetailsMobileComponent } from './pages/book/review-details-mobile/review-details-mobile.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { EventoSelectComponent } from './shared-components/evento-select/evento-select.component';
import { ToastsContainerComponent } from './shared-components/toasts-container/toasts-container.component';
import { CancelPopupComponent } from './pages/book/cancel-popup/cancel-popup.component';
import { TicketCardComponent } from './pages/book/ticket-card/ticket-card.component';
import { ValidateComponent } from './pages/validate/validate/validate.component';
import { OrderItemComponent } from './pages/validate/order-item/order-item.component';
import { HtmlDesignComponent } from './pages/html-design/html-design.component';
import { VatComponent } from './shared-components/vat/vat.component';
import { ChangePasswordComponent } from './shared-components/change-password/change-password.component';
import { MyTicketsComponent } from './pages/tickets/components/my-tickets/my-tickets.component';
import { PaginationComponent } from './shared-components/pagination/pagination.component';
import { SuccessHandlingComponent } from './shared-components/success-handling/success-handling.component';
import { ErrorHandlingComponent } from './shared-components/error-handling/error-handling.component';
import { MyTicketsTabsPage } from './pages/tickets/pages/my-tickets-tabs/my-tickets-tabs.page';
import { SalesInfoComponent } from './pages/book/sales-info/sales-info.component';
import { CustomCurrencyPipe } from './services/pipes/custom-currency.pipe';
import { VerifyEmailComponent } from './pages/verify-email/verify-email.component';
import { SharedHttpModule } from '@evento-ng/libs';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { Observable, of, timer } from 'rxjs';
import { mergeMap, retryWhen, take } from 'rxjs/operators';

export class RetryableTranslateHttpLoader implements TranslateLoader {
  private http: HttpClient;
  private prefix: string;
  private suffix: string;
  private maxRetries = 2;

  constructor(http: HttpClient, prefix: string, suffix: string) {
    this.http = http;
    this.prefix = prefix;
    this.suffix = suffix;
  }

  getTranslation(lang: string): Observable<any> {
    const url = `${this.prefix}${lang}${this.suffix}`;
    return this.http.get(url).pipe(
      retryWhen((errors) =>
        errors.pipe(
          mergeMap((error, attempt) => {
            if (attempt >= this.maxRetries || error.status !== 0) {
              return of(null);
            }
            return timer(1000 * (attempt + 1));
          }),
          take(2)
        )
      )
    );
  }
}

export function TranslationLoaderFactory(
  http: HttpClient
): RetryableTranslateHttpLoader {
  return new RetryableTranslateHttpLoader(http, '/assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    HomeComponent,
    MeetingCardComponent,
    ContactUsComponent,
    FooterComponent,
    LoginPopupComponent,
    EventDetailsComponent,
    SafeHTMLPipe,
    LocalizedDatePipe,
    ToVATPipe,
    CustomCurrencyPipe,
    EventoInputComponent,
    ErrorInputComponent,
    NewBookingComponent,
    BookTitleComponent,
    BookFooterComponent,
    CartComponent,
    CartItemComponent,
    CustomFormComponent,
    ErrorMessageComponent,
    NewChooseTicketComponent,
    ReviewCartComponent,
    ReviewDetailsComponent,
    ReviewDetailsMobileComponent,
    TicketCardComponent,
    EventoSelectComponent,
    ToastsContainerComponent,
    CancelPopupComponent,
    ValidateComponent,
    OrderItemComponent,
    HtmlDesignComponent,
    VatComponent,
    ChangePasswordComponent,
    MyTicketsComponent,
    PaginationComponent,
    SuccessHandlingComponent,
    ErrorHandlingComponent,
    MyTicketsTabsPage,
    SalesInfoComponent,
    VerifyEmailComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    NgbDropdownModule,
    NgbToastModule,
    FormsModule,
    NgbCarouselModule,
    CommonModule,
    ReactiveFormsModule,
    NgOtpInputModule,
    RecaptchaModule,
    NgxIntlTelInputModule,
    BrowserAnimationsModule,
    NgxSpinnerModule,
    CustomFormsModule,
    NgSelectModule,
    NgbToastModule,
    SharedHttpModule.forRoot(),
    NgxPermissionsModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: TranslationLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: 'ar',
      useDefaultLang: true,
    }),
  ],
  providers: [
    ReCaptchaV3Service,
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: '6LfVbQArAAAAAIKLDhD_Yk674x2VcpW4VwzTKkOo',
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
